import { useCallback, useEffect, useRef } from 'react';
import { ERROR_API_CALL } from '@/consts/events';
import {
  MAX_TRANSIENT_POLLS,
  POLL_INTERVAL_MS,
  POLL_TIMEOUT_MS,
} from '@/consts/eligibilityPolling';
import { useRegistration } from '@/context/RegistrationContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useRecaptchaMint } from '@/hooks/useRecaptchaMint';
import { cmsApiClient } from '@/services/cmsApiClient';
import type { ACCESSEligibilityResultCode, TrackType } from '@/types/registration';
import { toIsoBirthDate } from '@/utils/date';
import { normalizeMbi } from '@/utils/mbi';
import type { WregIntakeForm } from './screens/wregIntakeFields';
import { TERMINAL_POLL_STATUSES, errStatus, pollErrStatus, sleep, toCmsGender } from './wregCmsPoll';

const WREG_TRACK: TrackType = 'eCKM';

export type WregEligFailure = 'invalid-dob' | 'rejected' | 'failed' | 'cancelled';

export type WregEligOutcome =
  | { ok: true; code: ACCESSEligibilityResultCode; resumable: boolean }
  | { ok: false; reason: WregEligFailure };

export const useWregEligibility = () => {
  const { trackError } = useAnalytics();
  // Sent to the CMS Integration Service as the second condition on per-MBI sandbox routing: a named
  // test MBI reaches the CMS sandbox only when it arrives on a test promo code, which keeps test
  // registrations inside the capped test client. Same source as create-user (useWregSignup).
  // ?invite=true skips the promo lookup, so this can legitimately be ''.
  const { registrationData } = useRegistration();
  const promoCode = registrationData.promoCodeData?.code ?? '';
  const mintToken = useRecaptchaMint('wreg_eligibility');
  const cancelledRef = useRef(false);

  useEffect(
    () => () => {
      cancelledRef.current = true;
    },
    [],
  );

  const reportFailure = useCallback(
    (service: string, status: string | number) => {
      trackError({
        event: ERROR_API_CALL,
        properties: { service, track: WREG_TRACK, status },
      });
    },
    [trackError],
  );

  const submit = useCallback(
    async (form: WregIntakeForm): Promise<WregEligOutcome> => {
      const birthDate = toIsoBirthDate(form.dob);
      if (!birthDate) return { ok: false, reason: 'invalid-dob' };

      cancelledRef.current = false;
      let submissionId: string;
      try {
        const recaptchaToken = await mintToken('wreg_eligibility');
        const submitted = await cmsApiClient.postEligibility(
          {
            mbi: normalizeMbi(form.mbi),
            track: WREG_TRACK,
            patientName: { family: form.last.trim(), given: [form.first.trim()] },
            gender: toCmsGender(form.gender),
            birthDate,
            promoCode,
          },
          undefined,
          recaptchaToken,
        );
        if (!submitted.submissionId) {
          reportFailure('cms_eligibility', 'no-submission-id');
          return { ok: false, reason: 'failed' };
        }
        submissionId = submitted.submissionId;
      } catch (err) {
        const status = errStatus(err);
        reportFailure('cms_eligibility', status);
        return { ok: false, reason: status === 422 ? 'rejected' : 'failed' };
      }

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      let transientReported = false;
      let transientRun = 0;
      while (Date.now() < deadline) {
        if (cancelledRef.current) return { ok: false, reason: 'cancelled' };
        try {
          const result = await cmsApiClient.getEligibilityResult(submissionId);
          transientRun = 0;
          if (result.status === 'complete') {
            if (result.resultCode) {
              return { ok: true, code: result.resultCode, resumable: result.resumable === true };
            }
            reportFailure('cms_eligibility_poll', 'complete-without-code');
            return { ok: false, reason: 'failed' };
          }
          if (result.status === 'error' || result.status === 'timeout') {
            reportFailure('cms_eligibility_poll', result.status);
            return { ok: false, reason: 'failed' };
          }
        } catch (err) {
          const status = pollErrStatus(err);
          if (status !== undefined && TERMINAL_POLL_STATUSES.has(status)) {
            reportFailure('cms_eligibility_poll', status);
            return { ok: false, reason: 'failed' };
          }
          if (!transientReported) {
            transientReported = true;
            reportFailure('cms_eligibility_poll', status ?? 'unknown');
          }
          transientRun += 1;
          if (transientRun >= MAX_TRANSIENT_POLLS) {
            reportFailure('cms_eligibility_poll', 'transient-exhausted');
            return { ok: false, reason: 'failed' };
          }
        }
        await sleep(POLL_INTERVAL_MS);
      }
      reportFailure('cms_eligibility_poll', 'timeout');
      return { ok: false, reason: 'failed' };
    },
    [mintToken, promoCode, reportFailure],
  );

  return { submit };
};
